import {Component, Input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {TranslateService} from '@ngx-translate/core';
import {ConnectionStatus, ConnectionStatusOptions, IotDeviceLog} from '@models/adminstration/gateway/iot-device-log';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {NotificationService} from '@services/notification/notification.service';

@Component({
    selector: 'scitis-admin-gateway-log-iot',
    templateUrl: './gateway-log-iot.component.html',
    styleUrls: ['./gateway-log-iot.component.scss'],
    standalone: false
})
export class GatewayLogIotComponent implements OnInit {

  @Input()
  gateway: Gateway = new Gateway();

  logData: IotDeviceLog[] = [];
  disableRefreshButton: boolean = false;
  disableCSVExportButton: boolean = false;
  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  constructor(
      private gatewayService: GatewayService,
      private loadingIndicator: LoadingIndicatorService,
      private translate: TranslateService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private notificationService: NotificationService) {
  }

  private static mapConnectionStatus(logs: any[]) {
    for (const item of logs) {
      const lowercaseItem = item?.connectionStatus?.toLowerCase() || '';
      if (lowercaseItem === 'connected') {
        item.connectionStatus = ConnectionStatus.CONNECTED;
      }
      if (lowercaseItem === 'disconnected') {
        item.connectionStatus = ConnectionStatus.DISCONNECTED;
      }
      if (lowercaseItem === 'publish') {
        item.connectionStatus = ConnectionStatus.PUBLISH;
      }
    }
  }

  ngOnInit(): void {
    this.setLogs();
  }

  public isDisconnected(log: IotDeviceLog): boolean {
    return log?.connectionStatus?.toLowerCase() === ConnectionStatus.DISCONNECTED.toLowerCase();
  }

  public connectionStatusTooltip(log: IotDeviceLog): string {
    return log.message;
  }

  public getConnectionStatusClass(log: IotDeviceLog): string {
    switch (log?.connectionStatus?.toLowerCase()) {
      case ConnectionStatus.CONNECTED.toLowerCase():
        return 'text-success';
      case ConnectionStatus.DISCONNECTED.toLowerCase():
        return 'text-danger';
      default:
        return '';
    }
  }

  public handleRefreshTrigger(eventFilters): void {
    this.setLogs(eventFilters);
  }

  public msToTime(durationInMillis: number): string {
    const milliseconds = Number(durationInMillis);
    if (milliseconds === 0) {
      return '0 ms';
    }
    const seconds = milliseconds / 1000;
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor(seconds % (3600 * 24) / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.floor(milliseconds % 1000);

    const dDisplay = d > 0 ? d + 'd ' : '';
    const hDisplay = h > 0 ? h + 'h ' : '';
    const mDisplay = m > 0 ? m + 'm ' : '';
    const sDisplay = s > 0 ? s + 's ' : '';
    const msDisplay = ms > 0 ? ms + 'ms' : '';
    return dDisplay + hDisplay + mDisplay + sDisplay + msDisplay;
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.logData = this.tableHeaderSortableService.sortItems<IotDeviceLog>(this.logData, column, direction);
  }

  private setLogs(eventFilter: string[] = [ConnectionStatusOptions.CONNECTED, ConnectionStatusOptions.DISCONNECTED]) {

    if (!this.gateway?.iotCore?.registryId || this.gateway?.iotCore?.registryId.isNullOrWhitespace()) {
      const message = this.translate.instant('The gateway "#gateway" has invalid IotCore configuration. Cannot load connection logs.').replace('#gateway', this.gateway.id);
      this.notificationService.warning(message, '');
      this.logData = [];
      return;
    }

    this.loadingIndicator.show();
    this.disableRefreshButton = true;
    this.disableCSVExportButton = true;
    this.gatewayService.listConnectivityLogs(this.gateway.iotCore.registryId, this.gateway.id, eventFilter).subscribe(
        {
          next: (connectivityLogs: IotDeviceLog[]) => {
            GatewayLogIotComponent.mapConnectionStatus(connectivityLogs);
            this.logData = connectivityLogs;
            this.disableRefreshButton = false;
            this.disableCSVExportButton = false;
            this.loadingIndicator.hide();
          }, error: (error) => {
            console.log('Error loading gateway logs.', error);
            this.disableRefreshButton = false;
            this.disableCSVExportButton = false;
            this.loadingIndicator.hide();
          }
        });
  }
}
